/* eslint-disable no-unused-vars */
import { useEffect, useRef, useState } from "react";
import { TbArrowRight, TbInfoCircleFilled, TbPencil } from "react-icons/tb";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router-dom";
import styled from "styled-components";
import bookApi from "../../../app-api/books";
import { selectUser, userActions } from "../../../app/reducers/UserSlice";
import Button from "../../../components/Button";
import Checkbox from "../../../components/Checkbox";
import Input from "../../../components/Input";
import StepProgress from "../../../components/StepProgress";
import SubAction from "../../../components/SubAction";
import toast from "../../../components/Toast";
import {
  Body,
  Bold,
  Callout,
  Caption1,
  Subhead,
  Title3,
} from "../../../styles/typography";
import { EVENTS, track } from "../../../util/analytics";
import { FLOW_STEP_COUNT, STEPS } from "../../book-details/DetailsWizard";
import {
  COVER_COLOR_PALETTE,
  getOptionHex,
} from "../../book-details/optionsConfig";
import { formatDate } from "../../memories/components/ChapterDatePills";

const CONFIRMATIONS = [
  "I have reviewed and approved all text.",
  "I have reviewed and approved all photos and captions.",
  "I understand that revisions after design approval may affect timeline and cost.",
];

const PageContainer = styled.div`
  margin: 0 auto;
  margin-top: 90px;
  padding: 24px;
  background: rgb(${(p) => p.theme.colors.white});
  min-height: calc(100vh - 90px);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ConfirmCard = styled.div`
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled(Title3)`
  font-size: 24px;
  font-weight: 700;
`;

const SectionDescription = styled(Body)`
  color: rgb(${(p) => p.theme.colors.neutral400});
`;

const CheckBoxWrapper = styled.div`
  background-color: rgb(${(p) => p.theme.colors.white});
  border: 2px solid rgb(${(p) => p.theme.colors.neutral150});
  padding: 12px;
  display: grid;
  grid-template-rows: 1fr 1fr 1fr;
  gap: 12px;
  border-radius: 16px;
  span {
    top: -4px;
  }
  ${(p) =>
    p.$error && `span { border-color: rgb(${p.theme.colors.error400}); }`}
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  ${Callout} {
    color: rgb(${(p) => p.theme.colors.neutral400});
  }
`;

const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgb(${(p) => p.theme.colors.error600});
  margin-top: 4px;
`;

const DisabledOverlay = styled.div`
  position: absolute;
  inset: 0;
  cursor: not-allowed;
`;

const Divider = styled.div`
  height: 1px;
  background: rgb(${(p) => p.theme.colors.neutral150});
`;

const ReviewCard = styled.div`
  background-color: rgb(${(p) => p.theme.colors.white});
  border: 2px solid rgb(${(p) => p.theme.colors.neutral150});
  border-radius: 16px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReviewRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
  &:not(:last-child) {
    border-bottom: 2px solid rgb(${(p) => p.theme.colors.neutral150});
  }
  > :nth-child(2) {
    margin-left: auto;
  }
`;

const ReviewRowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ReviewValue = styled(Caption1)`
  color: rgb(${(p) => p.theme.colors.neutral400});
  word-break: break-word;
`;

const DetailValue = styled(Body)`
  color: rgb(${(p) => p.theme.colors.neutral600});
  word-break: break-word;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  @media only screen and (max-width: 599px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.div`
  background-color: rgb(${(p) => p.theme.colors.white});
  border: 2px solid rgb(${(p) => p.theme.colors.neutral150});
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const OptionImageRow = styled.div`
  display: flex;
`;

const OptionImage = styled.img`
  background: rgba(240, 237, 231, 1);
  width: 100%;
  height: 220px;
  object-fit: contain;
  flex: 1 1 0;
  min-width: 0;
  &:not(:last-child) {
    border-right: 2px solid rgb(${(p) => p.theme.colors.neutral150});
  }
`;

const SwatchPreview = styled.div`
  width: 100%;
  height: 220px;
  flex: 1 1 0;
  min-width: 0;
  background: ${(p) => p.$hex || "rgba(240, 237, 231, 1)"};
`;

const AddOnsImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: 1fr;
  gap: 2px;
  height: 220px;
  overflow: hidden;
  background: rgb(${(p) => p.theme.colors.neutral150});
`;

const AddOnsImage = styled.img`
  background: rgba(240, 237, 231, 1);
  width: 100%;
  height: 100%;
  min-height: 0;
  min-width: 0;
  object-fit: contain;
  display: block;

  &:only-child,
  &:first-child:nth-last-child(3) {
    grid-column: 1 / -1;
  }
`;

const AddOnsPlaceholder = styled.div`
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(240, 237, 231, 1);
`;

const OptionContent = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const OptionQuestion = styled(Body)`
  font-weight: 500;
  flex: 1 1 auto;
  min-width: 0;
`;

const OptionAnswer = styled(Bold)`
  color: rgb(${(p) => p.theme.colors.neutral600});
  word-break: break-word;
`;

const ScrollableReviewCard = styled(ReviewCard)`
  max-height: 280px;
  padding: 0;
  overflow: hidden;
`;

const ScrollableReviewCardInner = styled.div`
  max-height: 280px;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SignatureSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SubmitRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const ApproveSubmission = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  const { selectedMemories, selectedMemoriesDetails, bookDetails, clearDraft } =
    useOutletContext();

  const handleEditStep = (stepSlug) => {
    navigate(`/book/details/${stepSlug}?from=review`);
  };

  const textSteps = STEPS.filter((step) => !step.getImages);
  const imageSteps = STEPS.filter((step) => step.getImages);

  const [checks, setChecks] = useState([false, false, false]);
  const [name, setName] = useState("");
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fullName =
    `${user?.name?.first ?? ""} ${user?.name?.last ?? ""}`.trim();
  const allChecked = checks.every(Boolean);
  const nameIsEmpty = name.trim() === "";
  const nameMatches = name.trim().toLowerCase() === fullName.toLowerCase();
  const canSubmit = allChecked && nameMatches;
  const showError = hasAttemptedSubmit && !allChecked;

  const consentTrackedRef = useRef(false);
  useEffect(() => {
    if (canSubmit && !consentTrackedRef.current) {
      consentTrackedRef.current = true;
      track(EVENTS.BOOK_SUBMISSION_CONSENT_COMPLETED);
    }
  }, [canSubmit]);

  const toggleCheck = (idx) => {
    setChecks((prev) => prev.map((v, i) => (i === idx ? !v : v)));
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      setHasAttemptedSubmit(true);
      return;
    }
    if (!selectedMemories?.length) {
      toast({
        type: "error",
        message: "Please select at least one chapter for your book.",
      });
      return;
    }
    try {
      setIsSubmitting(true);
      // Upserts the book and creates the submission (with consent) in a single call
      const { bookId: bookID } = await bookApi.createSubmission({
        memoryID: selectedMemories,
        title: bookDetails.title,
        firstName: bookDetails.firstName || user?.name?.first || "",
        lastName: bookDetails.lastName || user?.name?.last || "",
        subtitle: bookDetails.subtitle,
        dedication: bookDetails.dedication,
        photoLayout: bookDetails.photoLayout,
        addOns: bookDetails.addOns,
        frontCoverStyle: bookDetails.frontCoverStyle,
        frontCoverColor: bookDetails.frontCoverColor,
        backCoverStyle: bookDetails.backCoverStyle,
        backCoverColor: bookDetails.backCoverColor,
        messageToDesigner: (bookDetails.messageToDesigner ?? "").trim(),
        consentText: JSON.stringify(CONFIRMATIONS),
      });
      track(EVENTS.BOOK_SUBMITTED, {
        memoryCount: selectedMemories.length,
        hasSubtitle: Boolean(bookDetails.subtitle?.trim()),
        hasDedication: Boolean(bookDetails.dedication?.trim()),
        photoLayout: bookDetails.photoLayout,
        addOnsCount: Array.isArray(bookDetails.addOns)
          ? bookDetails.addOns.length
          : 0,
        frontCoverStyle: bookDetails.frontCoverStyle,
        backCoverStyle: bookDetails.backCoverStyle,
      });
      clearDraft();
      navigate("/thank-you");
    } catch (error) {
      console.log("error submitting a book", error);
      toast({
        type: "error",
        message: "Unable to submit your book submission request.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    dispatch(userActions.principal());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageContainer>
      <ContentWrapper>
        <StepProgress step={FLOW_STEP_COUNT} total={FLOW_STEP_COUNT} />
        <ConfirmCard>
          <SectionTitle as="h2">Your chapters</SectionTitle>
          <SectionDescription>
            {selectedMemoriesDetails.length} chapter
            {selectedMemoriesDetails.length === 1 ? "" : "s"} selected for this
            book.
          </SectionDescription>
          <ScrollableReviewCard>
            <ScrollableReviewCardInner>
              {selectedMemoriesDetails.map((memory) => (
                <ReviewRow key={memory.memoryID}>
                  <ReviewRowText>
                    <Bold>{memory.memoryTitle}</Bold>
                    {memory.date?.created && (
                      <ReviewValue>
                        Created : {formatDate(memory.date.created)}
                      </ReviewValue>
                    )}
                  </ReviewRowText>
                </ReviewRow>
              ))}
            </ScrollableReviewCardInner>
          </ScrollableReviewCard>
        </ConfirmCard>

        <Divider />

        <ConfirmCard>
          <SectionTitle as="h2">Book details</SectionTitle>
          <SectionDescription>
            Review what you've entered. You can edit any field below.
          </SectionDescription>
          <ReviewCard>
            <ReviewRow>
              <ReviewRowText>
                <Body style={{ fontWeight: "500" }}>Author name</Body>
                <DetailValue>
                  {`${bookDetails.firstName || user?.name?.first || ""} ${
                    bookDetails.lastName || user?.name?.last || ""
                  }`.trim() || "—"}
                </DetailValue>
              </ReviewRowText>
              <SubAction
                style={{ marginLeft: "auto" }}
                iconLeft={<TbPencil size={20} />}
                onAction={() => navigate("/book/author-name")}
                aria-label="Edit author name"
                title="Edit author name"
              >
                Edit
              </SubAction>
            </ReviewRow>
            {textSteps.map((step) => (
              <ReviewRow key={step.key}>
                <ReviewRowText>
                  <Body style={{ fontWeight: "500" }}>{step.title}</Body>
                  <DetailValue>
                    {step.getSummary(bookDetails) ?? "—"}
                  </DetailValue>
                </ReviewRowText>
                <SubAction
                  style={{ marginLeft: "auto" }}
                  iconLeft={<TbPencil size={20} />}
                  onAction={() => handleEditStep(step.slug)}
                  aria-label={`Edit ${step.title}`}
                  title={`Edit ${step.title}`}
                >
                  Edit
                </SubAction>
              </ReviewRow>
            ))}
          </ReviewCard>
        </ConfirmCard>

        <Divider />

        <ConfirmCard>
          <SectionTitle as="h2">Design options</SectionTitle>
          <SectionDescription>
            Review your selections. You can edit any option below.
          </SectionDescription>
          <OptionsGrid>
            {imageSteps.map((step) => {
              const images = step.getImages ? step.getImages(bookDetails) : [];
              return (
                <OptionCard key={step.key}>
                  {step.key === "addOns" ? (
                    images.length > 0 ? (
                      <AddOnsImageGrid>
                        {images.map((src, idx) => (
                          <AddOnsImage key={idx} src={src} alt="" />
                        ))}
                      </AddOnsImageGrid>
                    ) : (
                      <AddOnsPlaceholder>
                        <Body>None selected</Body>
                      </AddOnsPlaceholder>
                    )
                  ) : (
                    images.length > 0 && (
                      <OptionImageRow>
                        {step.isSwatch ? (
                          <SwatchPreview
                            $hex={getOptionHex(
                              COVER_COLOR_PALETTE,
                              bookDetails[step.key],
                            )}
                          />
                        ) : (
                          images.map((src, idx) => (
                            <OptionImage key={idx} src={src} alt="" />
                          ))
                        )}
                      </OptionImageRow>
                    )
                  )}
                  <OptionContent>
                    <OptionHeader>
                      <OptionQuestion>{step.title}</OptionQuestion>
                      <SubAction
                        iconLeft={<TbPencil size={20} />}
                        onAction={() => handleEditStep(step.slug)}
                        aria-label={`Edit ${step.title}`}
                      >
                        Edit
                      </SubAction>
                    </OptionHeader>
                    <OptionAnswer>
                      {step.getSummary(bookDetails) ?? "—"}
                    </OptionAnswer>
                  </OptionContent>
                </OptionCard>
              );
            })}
          </OptionsGrid>
        </ConfirmCard>

        <Divider />

        <ConfirmCard>
          <SectionTitle as="h2">
            Before submitting, please confirm: <span>*</span>
          </SectionTitle>
          <SectionDescription>
            Please confirm you have reviewed your content.
          </SectionDescription>
          <CheckBoxWrapper $error={showError}>
            {CONFIRMATIONS.map((text, idx) => (
              <CheckboxRow key={idx} onClick={() => toggleCheck(idx)}>
                <Checkbox
                  checked={checks[idx]}
                  selectItem={() => toggleCheck(idx)}
                  // style={{ marginBottom: 0 }}
                />
                <Callout>{text}</Callout>
              </CheckboxRow>
            ))}
          </CheckBoxWrapper>
          {showError && (
            <ErrorRow>
              <TbInfoCircleFilled size={24} />
              <Subhead>Complete all confirmations to continue.</Subhead>
            </ErrorRow>
          )}
        </ConfirmCard>

        <Divider />

        <SignatureSection>
          <SectionTitle as="h3">
            Approval signature <span>*</span>
          </SectionTitle>
          <SectionDescription>
            Please type your full name exactly as shown:{" "}
            <strong>{fullName}</strong>{" "}
            <span style={{ whiteSpace: "nowrap" }}>(not case-sensitive)</span>
          </SectionDescription>
          <div style={{ position: "relative" }}>
            <Input
              label="YOUR NAME"
              value={name}
              onValueChange={(e) => setName(e.target.value)}
              disabled={!allChecked}
              errorCondition={hasAttemptedSubmit && allChecked && !nameMatches}
              errorMessage={
                nameIsEmpty
                  ? "This field is required."
                  : "Name does not match. Please type your full name."
              }
              style={{ marginTop: "12px" }}
            />
            {!allChecked && (
              <DisabledOverlay onClick={() => setHasAttemptedSubmit(true)} />
            )}
          </div>
        </SignatureSection>

        <SubmitRow>
          <Button
            variant="tertiary"
            onButtonClick={() => navigate("/memories")}
            style={{ flex: "0 0 auto" }}
          >
            Cancel
          </Button>
          <div style={{ position: "relative", flex: "1 1 240px" }}>
            <Button
              variant="primary"
              onButtonClick={handleSubmit}
              loading={isSubmitting}
              disabled={!canSubmit || isSubmitting}
              style={{ width: "100%" }}
            >
              Approve &amp; submit <TbArrowRight />
            </Button>
            {!canSubmit && !isSubmitting && (
              <DisabledOverlay onClick={() => setHasAttemptedSubmit(true)} />
            )}
          </div>
        </SubmitRow>
      </ContentWrapper>
    </PageContainer>
  );
};

export default ApproveSubmission;
